import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Container, { Column, Row } from '@/components/container/Container';
import CheckboxInput from '@/components/form-inputs/CheckboxInput';
import { TextHeadlineLarge } from '@/components/text/Texts';
import { OTHER_BUSINESS_TYPE, businessTypes } from '@/constants/businessTypes';
import { COMPANY_DETAILS_FORM } from '@/constants/forms';
import IdGeneratorService from '@/services/id-generator.service';
import type { FormProps } from '@/types';
import { NextButton } from '@/components/buttons/Buttons';
import { NEXT } from '@/constants/button-labels';
import { type CompanyDetailsFormData, defaultValues, schema } from '../../engine/company-details.model';
import { InputController } from '@/components/inputs/InputController';
import DropdownInput from '@/components/inputs/DropdownInput';
import TextInput from '@/components/inputs/TextInput';

const CompanyDetailsForm: React.FC<FormProps<CompanyDetailsFormData>> = ({ isLoading, onBack, onSubmit }) => {
	const form = useForm({
		defaultValues,
		resolver: yupResolver(schema),
		mode: "all"
	});

	const companySizes = [
		{ label: '1', value: '1' },
		{ label: '2-10', value: '2-10' },
		{ label: '11-50', value: '11-50' },
		{ label: '51-100', value: '51-100' },
		// { label: '1-10 employees', value: '1-10 employees' },
		// { label: '11-50 employees', value: '11-50 employees' },
		// { label: '51-100 employees', value: '51-100 employees' },
		// { label: '101-500 employees', value: '101-500 employees' },
		// { label: '501-1000 employees', value: '501-1000 employees' },
		// { label: '1001+ employees', value: '1001+ employees' },
	];

	return (
		<form
			id={new IdGeneratorService(COMPANY_DETAILS_FORM).formId}
			onSubmit={form.handleSubmit(onSubmit)}
			noValidate
		>
			<Column>
				<TextHeadlineLarge className="text-inverseSurface" text="Tell us about your company?" />
				<Container className="grid grid-cols-1 gap-4 pt-8">
					<InputController id="name" control={form.control}>
						<TextInput label="Company Name" />
					</InputController>
					<InputController id="address" control={form.control}>
						<TextInput label="Company Address" />
					</InputController>
					<InputController id="workEmail" control={form.control}>
						<TextInput label="Work Email" />
					</InputController>

					<Row className="gap-4">
						<InputController id="companySize" control={form.control}>
							<DropdownInput
								options={companySizes}
								label="Company Size"
								placeholder="Please select"
							/>
						</InputController>
						<InputController id="mobileNumber" control={form.control}>
							<TextInput label="Phone" keyfilter={/^[0-9+]*$/} />
						</InputController>
					</Row>

					<InputController id="natureOfBusiness" control={form.control}>
						<DropdownInput
							options={businessTypes}
							label="Nature of Business"
							placeholder="Please select"
						/>
					</InputController>

					{form.watch('natureOfBusiness') === OTHER_BUSINESS_TYPE.value && (
						<InputController id="natureOfBusinessOther" control={form.control}>
							<TextInput label="Other Nature of Business" keyfilter={/[\p{L} ]/u} />
						</InputController>
					)}

					<Container className="grid grid-cols-1">
						<CheckboxInput
							className='self-start w-12 h-12 shrink-0 items-center justify-center'
							rowClassName='gap-2'
							control={form.control}
							formName={COMPANY_DETAILS_FORM}
							id="agree"
						>
							<span className="block py-4 font-poppins text-label-x-small text-black">
								I agree, on behalf of my company, to the
								<Link to="#"
									className="font-poppins text-label-x-small text-tertiary underline"
									children=" Xpress for Business Terms and Conditions"
								/>
								, which govern our use of Xpress for Business products.
							</span>
						</CheckboxInput>
					</Container>
				</Container>
			</Column>

			<Container className="flex pt-[24px]  justify-end">
				<NextButton
					id="sign-up-next-button"
					className={` ${!form.formState.isDirty ? 'disabled-button' : 'enabled-button'}`}
					disabled={!form.formState.isValid || isLoading}
					label={NEXT}
					type="submit"
					withIcon={false}
					width=''
				/>

			</Container>
		</form>
	);
};

export default CompanyDetailsForm;
